import PropertyTypeFilter from "./PropertyTypeFilter";
import { DualRangeSlider } from "../../common/DualRangeSlider";
import BedsRangeSelector from "../../common/BedsRangeSelector";
import BathsRangeSelector from "../../common/BathsRangeSelector";
import { PropsWithChildren } from "react";
import {
  SQFT_MIN,
  SQFT_MAX,
  ACREAGE_MIN,
  ACREAGE_MAX,
  YEAR_BUILT_MIN,
} from "../../../constants/neighborhoodSearch";
import { getYearBuiltMax } from "../../../utils/neighborhoodSearch";
import { OWNER_OCCUPANCY_FILTER, OWNER_OCCUPANCY_OPTIONS, OwnerOccupancyFilter } from "../constants";
import Checkbox from "src/components/core/Checkbox";
import { Input } from "src/components/core/Input";
import { RadioGroup, RadioGroupItem } from "src/components/shadcn/RadioGroup";
import { Label } from "src/components/shadcn/Label";

const SQFT_STEP = 100;
const ACREAGE_STEP = 0.1;
const YEAR_BUILT_STEP = 1;

const FilterRow = ({
  children,
  label,
}: PropsWithChildren & { label: string }) => {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-gray-primary text-[14px]">{label}</span>
      <div className="py-2">{children}</div>
    </div>
  );
};

export type PropertyFiltersProps = {
  selectedPropertyTypes: string[];
  onSelectedPropertyTypesChange?: (selectedPropertyTypes: string[]) => void;
  beds: (number | undefined)[];
  onBedsChange?: (beds: (number | undefined)[]) => void;
  baths: (number | undefined)[];
  onBathsChange?: (baths: (number | undefined)[]) => void;
  sqft: (number | undefined)[];
  onSqftChange?: (sqft: (number | undefined)[]) => void;
  acreage: (number | undefined)[];
  onAcreageChange?: (acreage: (number | undefined)[]) => void;
  yearBuilt: (number | undefined)[];
  onYearBuiltChange?: (yearBuilt: (number | undefined)[]) => void;
  disabled?: boolean;
  isFavorite?: boolean;
  onIsFavoriteChange?: (isFavorite: boolean) => void;
  isHideDNCPhoneNumbers?: boolean;
  onIsHideDNCPhoneNumbersChange?: (isHideDNCPhoneNumbers: boolean) => void;
  isShowOnlyPrivatelyOwned?: boolean;
  onIsShowOnlyPrivatleyOwnedChange?: (
    isShowOnlyPrivatelyOwned: boolean
  ) => void;
  ownerOccupied?: OwnerOccupancyFilter;
  onOwnerOccupiedChange?: (ownerOccupied: OwnerOccupancyFilter) => void;
  searchByTags?: string;
  onSearchByTagsChange?: (searchByTags: string) => void;
  searchByNotes?: string;
  onSearchByNotesChange?: (searchByNotes: string) => void;
  isSuppressExtendedFilters?: boolean;
};

export const PropertyFilters = ({
  selectedPropertyTypes,
  onSelectedPropertyTypesChange,
  beds,
  onBedsChange,
  baths,
  onBathsChange,
  sqft,
  onSqftChange,
  acreage,
  onAcreageChange,
  yearBuilt,
  onYearBuiltChange,
  disabled = false,
  isFavorite,
  onIsFavoriteChange,
  isHideDNCPhoneNumbers,
  onIsHideDNCPhoneNumbersChange,
  isShowOnlyPrivatelyOwned,
  onIsShowOnlyPrivatleyOwnedChange,
  ownerOccupied = OWNER_OCCUPANCY_FILTER.ALL,
  onOwnerOccupiedChange,
  searchByTags,
  onSearchByTagsChange,
  searchByNotes,
  onSearchByNotesChange,
  isSuppressExtendedFilters = false,
}: PropertyFiltersProps) => {
  // Convert (number | undefined)[] to number[] for DualRangeSlider
  const convertToNumberArray = (arr: (number | undefined)[]): number[] => {
    return arr.map((val) => (val === undefined ? 0 : val));
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-6 w-full items-center">
        <div className="w-[90%]">
          <PropertyTypeFilter
            selectedPropertyTypes={selectedPropertyTypes}
            onPropertyTypesChange={onSelectedPropertyTypesChange}
          />
        </div>
        <div className="flex flex-col w-[80%] gap-6">
          <FilterRow label="Beds">
            <BedsRangeSelector
              value={beds as [number | undefined, number | undefined]}
              onChange={onBedsChange}
              showAny={true}
            />
          </FilterRow>
          <FilterRow label="Baths">
            <BathsRangeSelector
              value={baths as [number | undefined, number | undefined]}
              onChange={onBathsChange}
              showAny={true}
            />
          </FilterRow>
        </div>
        <div className="flex flex-col w-[80%] gap-10">
          <FilterRow label="Square Footage">
            <DualRangeSlider
              disabled={disabled}
              label
              showPlusForMax
              lableContenPos="left"
              value={convertToNumberArray(sqft)}
              onValueChange={([min, max]) => onSqftChange?.([min, max])}
              min={SQFT_MIN}
              max={SQFT_MAX}
              step={SQFT_STEP}
            />
          </FilterRow>
          <FilterRow label="Acreage">
            <DualRangeSlider
              disabled={disabled}
              label
              showPlusForMax
              lableContenPos="left"
              value={convertToNumberArray(acreage)}
              onValueChange={([min, max]) => onAcreageChange?.([min, max])}
              min={ACREAGE_MIN}
              max={ACREAGE_MAX}
              step={ACREAGE_STEP}
              formatNumber={(value) => {
                if (
                  value === null ||
                  value === undefined ||
                  typeof value !== "number" ||
                  isNaN(value)
                ) {
                  return "0.00";
                }
                return value.toFixed(2);
              }}
            />
          </FilterRow>
          <FilterRow label="Year Built">
            <DualRangeSlider
              disabled={disabled}
              label
              showPlusForMax
              lableContenPos="left"
              value={convertToNumberArray(yearBuilt)}
              onValueChange={([min, max]) => onYearBuiltChange?.([min, max])}
              min={YEAR_BUILT_MIN}
              max={getYearBuiltMax()}
              step={YEAR_BUILT_STEP}
              formatNumber={(value) => value}
              disableNumberFormatting={true}
            />
          </FilterRow>
        </div>
      </div>
      {!isSuppressExtendedFilters && (
        <div className="flex flex-col gap-10 mt-12">
          <div className="flex flex-col gap-2">
            <Label className="text-sm">Owner Status</Label>
            <RadioGroup
              value={ownerOccupied}
              onValueChange={(value) =>
                onOwnerOccupiedChange?.(value as OwnerOccupancyFilter)
              }
              className="flex flex-col gap-2"
            >
              {OWNER_OCCUPANCY_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value={option.value} 
                    id={`owner-${option.value}`} 
                  />
                  <Label
                    htmlFor={`owner-${option.value}`}
                    className="font-normal cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Checkbox
              label="Show Do Not Call phone numbers"
              checked={!isHideDNCPhoneNumbers}
              onCheckedChange={(checked) =>
                onIsHideDNCPhoneNumbersChange?.(!Boolean(checked))
              }
            />
            <Checkbox
              label="Only show favorites"
              checked={isFavorite}
              onCheckedChange={(checked) =>
                onIsFavoriteChange?.(Boolean(checked))
              }
            />
            <Checkbox
              label="Only Show Privately Owned"
              checked={isShowOnlyPrivatelyOwned}
              onCheckedChange={(checked) =>
                onIsShowOnlyPrivatleyOwnedChange?.(Boolean(checked))
              }
            />
          </div>
          <div className="flex flex-col gap-4">
            <Input
              placeholder="Search by tags"
              value={searchByTags}
              onChange={(e) => onSearchByTagsChange?.(e.target.value)}
            />
            <Input
              placeholder="Search by notes"
              value={searchByNotes}
              onChange={(e) => onSearchByNotesChange?.(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};
